import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import {
  AudienceSet,
  Brand,
  CampaignAdSet,
  ChangeEventType,
  DemographicsSet,
  FbTargetSearch,
  IGeolocationSet,
} from '../../../../../types';
import { FloatingButton, PrimaryButton } from '../../../../Buttons';
import { BusinessType } from '../../../../../utils/helpers/BrandHelpers';
import { numberShortener } from '../../../../../utils/numberFormatter';
import Spinner from '../../../../Spinner';
import { FBOptimizationGoal } from '../../../../../utils/helpers/facebookAdsHelper';
import { getFbTargetingReachEstimate } from '../../../../../services/ads';
import {
  createOrUpdateDemographicsSet,
  getBrandAudienceSets,
  getBrandLocationSets,
  getSingleBrandDemographicsSets,
} from '../../../../../services/assets';
import { useForm } from 'react-hook-form';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import { HtmlTooltip } from '../../../../Tooltip';
import {
  geolocationToText,
  parsedDetailedTargeting,
  targetsToText,
} from '../../../../../utils/helpers/AssetHelpers';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import PageHeader from '../../../../PageHeader';
import PopupModal from '../../../../Modal';
import ModalHeader from '../../../../Modal/ModalHeader';

interface DemographicsSetFormProps {
  brand: Brand;
  onClose: () => void;
  edit?: boolean;
  demographicsSetId?: string;
  isCreateAds?: boolean;
  // If used in Create Campaign Form
  demographicsSets?: DemographicsSet[];
  setDemographicsSets?: Dispatch<SetStateAction<DemographicsSet[]>>;
  setFilteredDemographicsSets?: Dispatch<SetStateAction<DemographicsSet[]>>;
  onSelectDemographicsSet?: (demographicsSet: DemographicsSet) => void;
  onClearDemographicsSet?: () => void;
  selectedAudienceSet?: AudienceSet | null;
}

const DemographicsSetForm: FC<DemographicsSetFormProps> = ({
  brand,
  onClose,
  edit,
  demographicsSetId,
  isCreateAds = false,
  demographicsSets = [],
  setDemographicsSets = null,
  setFilteredDemographicsSets = null,
  onSelectDemographicsSet = null,
  onClearDemographicsSet = null,
  selectedAudienceSet = null,
}) => {
  const isAIEnabled =
    selectedAudienceSet?.targets?.targeting_automation?.advantage_audience ===
    1;

  const {
    formState: { errors },
    register,
    handleSubmit,
    clearErrors,
  } = useForm({ mode: 'onSubmit' });
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<DemographicsSet>({
    name: '',
    description: '',
    primary: true,
    genders: [1, 2],
    age_min: brand?.businessType === BusinessType.FRANDEV ? 25 : 18,
    age_max: brand?.businessType === BusinessType.FRANDEV ? 59 : 64,
    minReach: 0,
    maxReach: 0,
  });
  const [reachLoading, setReachLoading] = useState<boolean>(false);
  const [reachEstimate, setReachEstimate] = useState({
    users_lower_bound: 0,
    users_upper_bound: 0,
  });
  const [reachError, setReachError] = useState<string>(null);
  const [defaultLocationSet, setDefaultLocationSet] =
    useState<IGeolocationSet>(null);
  const [defaultAudienceSet, setDefaultAudienceSet] =
    useState<AudienceSet>(null);
  const [confirmEdit, setConfirmEdit] = useState<boolean>(false);

  useEffect(() => {
    if (edit && demographicsSetId) getDemographicsSet();
  }, [edit, demographicsSetId]);

  useEffect(() => {
    if (brand) {
      getPrimaryLocationSet();
      getPrimaryAudienceSet();
    }
  }, [brand]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const targets = parsedDetailedTargeting(defaultAudienceSet?.targets);

      let temp: any = {
        ...targets,
        genders: formValues?.genders,
        age_min: formValues?.age_min,
        age_max: formValues?.age_max,
        geo_locations: defaultLocationSet?.geolocations || {
          countries: [{ key: 'US', type: 'country' }],
        },
        excluded_geo_locations: defaultLocationSet?.excludedGeolocations || {},
      };

      getFacebookTargetReach(temp);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [formValues?.genders, formValues?.age_min, formValues?.age_max]);

  const buildAge = (start: number, end: number, step: number) => {
    return Array.from(
      Array.from(Array(Math.ceil((end - start) / step)).keys()),
      (x) => start + x * step,
    );
  };

  const getFacebookTargetReach = async (targeting: FbTargetSearch) => {
    try {
      setReachLoading(true);

      const response = await getFbTargetingReachEstimate({
        brandId: brand?._id,
        targets: targeting,
        optimizationGoal: FBOptimizationGoal.LeadGeneration,
      });

      setReachEstimate(response.data);
      setReachError(null);
    } catch (error: any) {
      setReachEstimate({
        users_lower_bound: 0,
        users_upper_bound: 0,
      });
      const err = error.response.data;
      setReachError(err?.message);
    } finally {
      setReachLoading(false);
    }
  };

  const getPrimaryLocationSet = async () => {
    try {
      const response = await getBrandLocationSets({
        brandId: brand._id,
        primary: true,
      });

      setDefaultLocationSet(response.data[0]);
    } catch (error: any) {
      console.log(error);
    }
  };

  const getPrimaryAudienceSet = async () => {
    try {
      const response = await getBrandAudienceSets({
        brandId: brand._id,
        primary: true,
      });

      setDefaultAudienceSet(response.data[0]);
    } catch (error: any) {
      console.log(error);
    }
  };

  const getDemographicsSet = async () => {
    try {
      const response = await getSingleBrandDemographicsSets(
        brand?._id,
        demographicsSetId,
      );

      setFormValues(response.data);
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleOnSubmit = async () => {
    try {
      if (reachError) {
        setLoading(false);

        return dispatch(
          toggleAlert({
            toggle: true,
            message: reachError,
            type: 'error',
          }),
        );
      }

      if (formValues?.usageCount > 0 && !confirmEdit && edit) {
        return setConfirmEdit(true);
      }

      setLoading(true);

      let temp: DemographicsSet = {
        ...formValues,
        minReach: reachEstimate?.users_lower_bound || 0,
        maxReach: reachEstimate?.users_upper_bound || 0,
      };

      const { usageCount, adsUsage, ...params } = temp;

      const response = await createOrUpdateDemographicsSet({
        brandId: brand?._id,
        body: params,
        demographicsSetId: edit ? demographicsSetId : null,
      });

      const data: DemographicsSet = response.data;

      if (isCreateAds) {
        if (edit) {
          let temp: DemographicsSet[] = [];
          demographicsSets.forEach((demographicsSet: DemographicsSet) => {
            if (demographicsSet._id === data._id) {
              demographicsSet = data;
            }

            temp = [...temp, demographicsSet];
          });

          setDemographicsSets(temp);
          setFilteredDemographicsSets(temp);
        } else {
          setDemographicsSets((prev) => [data, ...prev]);
          setFilteredDemographicsSets((prev) => [data, ...prev]);
        }

        const isSkipSetDemographicsSet =
          isAIEnabled && data?.age_min > 25 && data?.age_max < 65;

        if (isSkipSetDemographicsSet) {
          onClearDemographicsSet();
        } else {
          onSelectDemographicsSet(data);
        }
      }

      dispatch(
        toggleAlert({
          toggle: true,
          message: 'Your demographics set has been saved successfully',
        }),
      );

      onClose();
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (e: ChangeEventType) => {
    clearErrors(e.target.name);
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleOnClickCancel = () => {
    if (isCreateAds) onClearDemographicsSet();
    onClose();
  };

  const handleCloseConfirmEdit = () => {
    setConfirmEdit((prev) => !prev);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {isCreateAds ? (
          <Grid item xs={12}>
            <PageHeader
              title={`${edit ? 'EDIT' : 'CREATE'} DEMOGRAPHICS SET`}
              textAlign="left"
              marginBottom="0"
            />
          </Grid>
        ) : null}

        <Grid item xs={12} sm={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('name', {
                  required: true,
                })}
                fullWidth
                required
                variant="standard"
                type="text"
                id="name"
                name="name"
                label="Demographics Set Name"
                onChange={handleOnChange}
                value={formValues?.name}
                InputLabelProps={{ shrink: true }}
                size="small"
                autoFocus
                error={!!errors?.name}
                helperText={errors?.name && 'Demographics set name is required'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="standard"
                type="text"
                id="description"
                name="description"
                label="Description"
                onChange={handleOnChange}
                value={formValues?.description}
                InputLabelProps={{ shrink: true }}
                size="small"
                multiline
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={formValues?.primary} />
                }
                label="Set as default audience set"
                value={formValues?.primary}
                onChange={(e: any) => {
                  setFormValues({
                    ...formValues,
                    primary: e.target.checked,
                  });
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ flexDirection: 'column', display: 'flex' }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Estimated Audience Size:{' '}
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ marginLeft: '10px' }}
                >
                  {reachLoading ? (
                    <Spinner />
                  ) : (
                    `${numberShortener(
                      reachEstimate.users_lower_bound,
                    )} - ${numberShortener(reachEstimate.users_upper_bound)}`
                  )}
                </Typography>
              </Typography>

              <Typography
                variant="caption"
                sx={{ alignItems: 'center', display: 'flex' }}
              >
                <HtmlTooltip
                  title={`Geo Locations\n${geolocationToText(
                    defaultLocationSet,
                  )}\nAudiences\n${targetsToText(defaultAudienceSet?.targets)}`}
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  <InfoTwoToneIcon
                    sx={{
                      fontSize: '18px',
                      color: '#096F4D',
                    }}
                  />
                </HtmlTooltip>

                <Box sx={{ marginLeft: '5px' }}>
                  Computed based on default geo locations and audience sets
                </Box>
              </Typography>

              {reachError ? (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 'bold', color: 'red' }}
                >
                  {reachError}
                </Typography>
              ) : null}
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={1} my={2}>
                <Grid item xs={6}>
                  <TextField
                    id={`minAge-0`}
                    select
                    label="Min Age"
                    value={formValues.age_min?.toString() || 18}
                    name="age_min"
                    onChange={(e: any) => {
                      setFormValues({
                        ...formValues,
                        age_min: parseInt(e.target.value),
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  >
                    {buildAge(18, 66, 1).map((text: any) => (
                      <MenuItem key={text} value={text}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    select
                    label="Max Age"
                    value={formValues?.age_max || 64}
                    name="age_max"
                    onChange={(e: any) => {
                      setFormValues({
                        ...formValues,
                        age_max: parseInt(e.target.value),
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  >
                    {buildAge(18, 66, 1).map((text: any) => (
                      <MenuItem key={text} value={text}>
                        {text === 65 ? `${text}+` : text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormControl>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 'bold',
                    justifyContent: 'flex-start',
                    color: '#096F4D',
                  }}
                  mb={1}
                >
                  Select your target gender
                </Typography>

                <RadioGroup
                  row
                  aria-labelledby="genders"
                  name="genders"
                  value={formValues?.genders}
                  onChange={(e: ChangeEventType) => {
                    let value: number[] = [];
                    if (e.target.value === '1,2') {
                      value = e.target.value.split(',').map((value: string) => {
                        return parseInt(value);
                      });
                    } else {
                      value = [parseInt(e.target.value)];
                    }

                    setFormValues({ ...formValues, genders: value });
                  }}
                  sx={{ flexDirection: 'column' }}
                  defaultValue="1,2"
                >
                  <FormControlLabel
                    value="1,2"
                    control={
                      <Radio
                        size="small"
                        defaultChecked={
                          formValues?.genders?.length === 0 ||
                          formValues?.genders?.length === 2
                        }
                      />
                    }
                    label="All"
                  />

                  <FormControlLabel
                    value="1"
                    control={<Radio size="small" />}
                    label="Male"
                    defaultChecked={
                      formValues?.genders ? false : formValues?.genders[0] === 1
                    }
                  />

                  <FormControlLabel
                    value="2"
                    control={<Radio size="small" />}
                    label="Female"
                    defaultChecked={
                      formValues?.genders ? false : formValues?.genders[0] === 2
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <FloatingButton
        title="Save"
        onClick={handleSubmit(handleOnSubmit)}
        loading={loading}
        bottom="60px"
        right="180px"
        fixed
      />

      <FloatingButton
        title="Cancel"
        onClick={handleOnClickCancel}
        bottom="60px"
        right="85px"
        fixed
        theme="red"
      />

      <PopupModal open={confirmEdit} handleClose={handleCloseConfirmEdit}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ModalHeader title="Confirm Edit Template" />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{ textAlign: 'center', fontWeight: 'bold' }}
            >
              Modifying this template will affect other ad sets that use it.
              Confirm edit?
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}
            >
              {formValues?.adsUsage
                ?.map((adset: CampaignAdSet) => `${adset.name} (${adset.id})`)
                ?.join('\n')}
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          sx={{ position: 'sticky', bottom: 0 }}
          mt={2}
        >
          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <PrimaryButton
              title="Cancel"
              type="button"
              handleOnClick={handleCloseConfirmEdit}
              loading={loading}
              marginRight5
            />

            <PrimaryButton
              title="Confirm"
              type="button"
              handleOnClick={handleOnSubmit}
              loading={loading}
            />
          </Grid>
        </Grid>
      </PopupModal>
    </Box>
  );
};

export default DemographicsSetForm;
